import { useEffect, useRef } from 'react';
import type { DailyCall, DailyParticipant } from '@daily-co/daily-js';

export function DailyVideoTrack({
  callFrame,
  participantId,
  muted = false,
  className,
}: {
  callFrame: unknown;
  participantId: string | 'local';
  muted?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const call = callFrame as DailyCall | null;
    if (!call) return;

    const attach = () => {
      const participants = call.participants() as Record<string, DailyParticipant>;
      const participant = participantId === 'local'
        ? participants.local
        : Object.values(participants).find(
          (entry) => entry.session_id === participantId || entry.user_id === participantId,
        );

      if (!participant) return;

      const trackInfo = (participant as unknown as {
        tracks?: { video?: { persistentTrack?: MediaStreamTrack; track?: MediaStreamTrack } };
      }).tracks?.video;
      const track = trackInfo?.persistentTrack ?? trackInfo?.track;

      if (track && videoRef.current) {
        videoRef.current.srcObject = new MediaStream([track]);
      } else if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    attach();
    call.on('track-started', attach);
    call.on('participant-updated', attach);

    return () => {
      call.off('track-started', attach);
      call.off('participant-updated', attach);
    };
  }, [callFrame, participantId]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      className={className}
    />
  );
}